import React, { FC } from 'react';
import { WindowWidthContext } from '@minecraft.utils';
import { AddressValidationForm } from '../AddressValidation';
import { AddPaymentProps } from './types';
import { Purchase } from '../types';
import { WalletPicker } from './PaymentForm/WalletPicker';
import { RegistrationCheckoutForm } from './RegistrationCheckoutForm';
import { SelectPlanSm } from './SelectPlanSm';
import { useRegistrationAddPayment } from './useRegistrationAddPayment';

export const RegistrationAddPayment: FC<AddPaymentProps> = ({
  addPaymentError,
  addPaymentLoading,
  onAddPaymentOption,
  onUpdatePaymentOption,
  threeDActionContext,
  onThreeDActionError,
  currencyCode,
  countryCode,
  paywallLocation,
  allowCancel,
  onCompletePurchase,
  selectedPlan,
  currentPurchase,
  completedPurchase,
  completePurchaseLoading,
  completePurchaseError,
  selectedPaymentOption,
  wallet,
  onSelectPaymentOption,
  onNext,
  setIsAnnual,
  isAnnual,
  plans,
  setSelectedPlan,
  onPaymentCompleteRedirect,
  currentProfile,
  disableTrial = false,
  onApplyCoupon,
  couponLoading,
  applyCouponError,
  onRemoveCoupon,
  showApplePayButton,
  onClickApplePay,
  requestRecurlyTokenAndValidate,
  handlePaymentPriorityChange,
  paymentMethodFormRef,
  paymentMethodFormikRef,
  setShowAddPaymentMethod,
  showAddPaymentMethod,
  discountType,
  onCancel,
}) => {
  const isMobile = WindowWidthContext.useIsMobile();

  const {
    walletPickerProps,
    requiresBillingAddressBeforePurchase,
    handleRegistrationSubmit,
    handleAddCardDuringCheckout,
    handleCheckoutPayPalToken,
    pendingAddressIssue,
    resolveAddressIssue,
    dismissAddressIssue,
    addressValidationEvents,
  } = useRegistrationAddPayment({
    showAddPaymentMethod,
    setShowAddPaymentMethod,
    threeDActionContext,
    wallet,
    selectedPaymentOption,
    onSelectPaymentOption,
    onUpdatePaymentOption,
    onAddPaymentOption,
    paymentMethodFormikRef,
    requestRecurlyTokenAndValidate,
    selectedPlan,
    currentPurchase,
    onCompletePurchase,
    currentProfile,
    onPaymentCompleteRedirect,
    onNext,
  });

  return (
    <>
      {isMobile && (
        <SelectPlanSm
          plans={plans}
          discountType={discountType}
          selectedPlan={selectedPlan}
          setSelectedPlan={setSelectedPlan}
          isAnnual={isAnnual}
          setIsAnnual={setIsAnnual}
          countryCode={countryCode}
          currencyCode={currencyCode}
          loading={!selectedPlan || addPaymentLoading}
        />
      )}
      <WalletPicker
        isWalletVisible={walletPickerProps.isWalletVisible}
        isBillingAddressEditable={walletPickerProps.isBillingAddressEditable}
        wallet={walletPickerProps.wallet}
        selectedPaymentOption={walletPickerProps.selectedPaymentOption}
        onSelectPaymentOption={walletPickerProps.onSelectPaymentOption}
        setShowAddPaymentMethod={walletPickerProps.setShowAddPaymentMethod}
        onUpdatePaymentOption={walletPickerProps.onUpdatePaymentOption}
        funnelEvents={walletPickerProps.funnelEvents}
        addressValidationEvents={walletPickerProps.addressValidationEvents}
        onAddressIssueDetected={walletPickerProps.onAddressIssueDetected}
      />
      <RegistrationCheckoutForm
        onCancel={onCancel}
        addPaymentError={addPaymentError}
        addPaymentLoading={addPaymentLoading}
        wallet={wallet}
        allowCancel={allowCancel}
        onAddPaymentOption={onAddPaymentOption}
        requestRecurlyTokenAndValidate={requestRecurlyTokenAndValidate}
        threeDActionContext={threeDActionContext}
        onThreeDActionError={onThreeDActionError}
        currencyCode={currencyCode}
        countryCode={countryCode}
        paywallLocation={paywallLocation}
        plans={plans}
        selectedPlan={selectedPlan}
        currentPurchase={currentPurchase as Purchase}
        completePurchaseError={completePurchaseError}
        selectedPaymentOption={selectedPaymentOption}
        completedPurchase={completedPurchase}
        completePurchaseLoading={completePurchaseLoading}
        setIsAnnual={setIsAnnual}
        isAnnual={isAnnual}
        disableTrial={disableTrial}
        onApplyCoupon={onApplyCoupon}
        couponLoading={couponLoading}
        applyCouponError={applyCouponError}
        onRemoveCoupon={onRemoveCoupon}
        showApplePayButton={showApplePayButton}
        onClickApplePay={onClickApplePay}
        onSubmit={handleRegistrationSubmit}
        onAddCardDuringCheckout={handleAddCardDuringCheckout}
        onPayPalToken={handleCheckoutPayPalToken}
        handlePaymentPriorityChange={handlePaymentPriorityChange}
        paymentMethodFormRef={paymentMethodFormRef}
        paymentMethodFormikRef={paymentMethodFormikRef}
        setShowAddPaymentMethod={setShowAddPaymentMethod}
        showAddPaymentMethod={showAddPaymentMethod}
        discountType={discountType}
        allowPurchase={!requiresBillingAddressBeforePurchase}
      />
      {pendingAddressIssue && (
        <AddressValidationForm
          result={pendingAddressIssue.result}
          currentAddress={pendingAddressIssue.currentAddress}
          lastCheckedInput={pendingAddressIssue.lastCheckedInput}
          onSubmit={resolveAddressIssue}
          onDismiss={dismissAddressIssue}
          onSuggestionSelected={() => addressValidationEvents.trackAddressValidationSuggestionClicked()}
        />
      )}
    </>
  );
};
